import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, Users } from "lucide-react";
import { BrandLogo } from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { HERO_BG } from "@/lib/brand";
import { GoogleAuthButton, OrDivider } from "@/components/GoogleAuthButton";
import { track } from "@/lib/analytics";

const ROLE_LABEL = {
  admin: "Admin — full access, can manage the team",
  member: "Member — can create and edit projects",
  viewer: "Viewer — read-only access",
};

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/team/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((err) => setLoadError(apiError(err.response?.data?.detail) || "This invitation is no longer valid."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post(`/team/invite/${token}/accept`, { name, password });
      localStorage.setItem("bs_token", data.token);
      track("sign_up", { method: "invite" });
      await refreshMe();
      navigate("/app");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const acceptWithGoogle = () => {
    localStorage.setItem("bs_invite_token", token);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="mb-8"><BrandLogo /></div>
        <div className="max-w-sm w-full" data-testid="invite-accept-page">
          {loadError ? (
            <div className="space-y-4">
              <h1 className="font-display text-3xl font-bold text-slate-900">Invitation unavailable</h1>
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="invite-load-error">{loadError}</div>
              <Link to="/login" className="text-blue-600 font-medium text-sm">Go to login</Link>
            </div>
          ) : !invite ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading your invitation…</div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full px-3 py-1 mb-4">
                <Users className="h-3.5 w-3.5" /> Team invitation
              </div>
              <h1 className="font-display text-3xl font-bold text-slate-900">Join {invite.workspace}</h1>
              <p className="text-slate-500 mt-2 text-sm">
                {invite.invited_by ? `${invite.invited_by} invited you` : "You've been invited"} to join as{" "}
                <span className="font-semibold text-slate-700" data-testid="invite-role">{ROLE_LABEL[invite.role] || invite.role}</span>.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="invite-accept-form">
                {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="invite-accept-error">{error}</div>}
                <GoogleAuthButton label="Accept with Google" onClick={acceptWithGoogle} disabled={loading} />
                <p className="text-xs text-slate-400 text-center -mt-1">Use the Google account for {invite.email}</p>
                <OrDivider />
                <div>
                  <Label>Email</Label>
                  <Input value={invite.email} disabled className="mt-1 bg-slate-50 text-slate-400" data-testid="invite-email" />
                </div>
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="password">Choose a password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="invite-password" className="mt-1" />
                </div>
                <Button type="submit" disabled={loading} data-testid="invite-accept-submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11">
                  {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Accept & join workspace
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(22,48,92,0.85), rgba(37,99,235,0.55))" }} />
        <div className="relative h-full flex items-end p-12">
          <p className="text-white font-display text-2xl font-semibold max-w-sm">Estimating and progress billing, done together as a team.</p>
        </div>
      </div>
    </div>
  );
};

export default InviteAccept;
